import { useCallback, useEffect, useState } from "react";
import { CredentialWithId } from "../../../../pages/credentialsConfigration/types/credentialTypes";

interface UseTouchedProps {
  isOpen: boolean;
  isEditMode: boolean;
  credentialData: CredentialWithId | null;
}

/**
 * Owns the per-field "touched" map used to gate validation errors so they
 * only appear after a field has been interacted with.
 *
 * Extracted out of `useFieldValidation` so that `useMfaBranch` can also
 * mark/clear touched flags when the user flips the MFA branch, without
 * either hook having to depend on the other (which would create a cycle:
 * validation needs `mfaMethod`, branch-switching needs touched callbacks).
 *
 * The map is reset to `{}` every time the modal opens, mirroring the
 * original behavior of the consolidated effect in `credentialDetailsModal`.
 */
export const useTouched = ({
  isOpen,
  isEditMode,
  credentialData,
}: UseTouchedProps) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    setTouched({});
  }, [isOpen, isEditMode, credentialData]);

  /**
   * Marks a single field touched. Idempotent — no state update if the
   * field is already touched, to avoid unnecessary rerenders.
   */
  const markTouched = useCallback((field: string) => {
    setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  /**
   * Removes the touched flag for a single field. Used when an MFA branch
   * switch makes a field irrelevant so its stale error doesn't linger.
   */
  const clearTouched = useCallback((field: string) => {
    setTouched((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Replaces the touched map with one that flags every supplied field.
   * Used by the save handler when the form is invalid: all current errors
   * become visible at once instead of trickling in per-keystroke.
   */
  const markFieldsTouched = useCallback((fields: string[]) => {
    setTouched(
      fields.reduce<Record<string, boolean>>((acc, field) => {
        acc[field] = true;
        return acc;
      }, {}),
    );
  }, []);

  return { touched, markTouched, clearTouched, markFieldsTouched };
};
